import API from '@/api/apiCreate'
import { getParams } from '@/api/getParams'
import { isError } from '@/helpers/checkQuery'
import { ResponseWrapper } from '@/api/types/base.types'
import {
  SetTaskTrackerTaskStickerValueRequest,
  TaskTrackerBoardCard,
  TaskTrackerFilter,
} from '../store/taskTracker.types'
import {
  mapBoardCardFromApi,
  mapBoardFromApi,
  mapBoardStickersFromApi,
  mapFiltersFromApi,
} from './taskTracker.api.mappers'
import { getApiErrorMessage, TASK_TRACKER_API_REQUEST_CONFIG } from './taskTracker.api.utils'
import {
  ApiBoardCard,
  ApiTaskTrackerFiltersCreateResponse,
  ApiTaskTrackerFiltersDeleteResponse,
  ApiTaskTrackerFiltersIndexResponse,
  ApiTaskTrackerFiltersUpdateResponse,
  ApiTaskTrackerBoardStickersResponse,
} from './taskTracker.api.types'
import {
  CreateTaskTrackerFilterRequest,
  SetTaskTrackerBoardStickerPositionRequest,
  SetTaskTrackerBoardStickerRequiredRequest,
  SetTaskTrackerBoardStickerVisibilityRequest,
  TaskTrackerBoardStickersDetails,
  TaskTrackerFilterSelectOption,
  UpdateTaskTrackerFilterRequest,
} from '../store/taskTracker.types'

const mapFilterOptionsBody = (
  options: TaskTrackerFilterSelectOption[] | undefined,
) =>
  (options ?? []).map((option) => ({
    label: option.label.trim(),
    color: option.color,
  }))

const mapCreateFilterBody = (payload: CreateTaskTrackerFilterRequest) => {
  const body: Record<string, unknown> = {
    field_type: payload.fieldType,
    title: payload.title.trim(),
    color: payload.color,
    is_required: payload.isRequired,
  }

  if (payload.fieldType === 'select') {
    body.options = mapFilterOptionsBody(payload.options)
  }

  return body
}

const mapUpdateFilterBody = (payload: UpdateTaskTrackerFilterRequest) => {
  const body: Record<string, unknown> = {
    field_type: payload.fieldType,
    title: payload.title.trim(),
    color: payload.color,
    is_required: payload.isRequired,
    options:
      payload.fieldType === 'select'
        ? mapFilterOptionsBody(payload.options)
        : [],
  }

  if (payload.position != null) {
    body.position = payload.position
  }

  return body
}

export const taskTrackerFieldsApi = {
  async getFilters(): Promise<TaskTrackerFilter[]> {
    const res = await API.get<
      ResponseWrapper<ApiTaskTrackerFiltersIndexResponse>
    >('task-tracker-fields/index', TASK_TRACKER_API_REQUEST_CONFIG)
    if (isError(res)) {
      throw new Error(
        getApiErrorMessage(res, 'Не удалось загрузить каталог стикеров'),
      )
    }

    return mapFiltersFromApi(res.data?.data?.stickers)
  },

  async createFilter(
    payload: CreateTaskTrackerFilterRequest,
  ): Promise<TaskTrackerFilter[]> {
    const res = await API.post<ResponseWrapper<ApiTaskTrackerFiltersCreateResponse>>(
      'task-tracker-fields/create',
      mapCreateFilterBody(payload),
      TASK_TRACKER_API_REQUEST_CONFIG,
    )
    if (isError(res)) {
      throw new Error(getApiErrorMessage(res, 'Не удалось создать стикер'))
    }

    return mapFiltersFromApi(res.data?.data?.stickers)
  },

  async updateFilter(
    filterId: number,
    payload: UpdateTaskTrackerFilterRequest,
  ): Promise<TaskTrackerFilter[]> {
    const res = await API.post<ResponseWrapper<ApiTaskTrackerFiltersUpdateResponse>>(
      `task-tracker-fields/update${getParams({ field_setting_id: filterId })}`,
      mapUpdateFilterBody(payload),
      TASK_TRACKER_API_REQUEST_CONFIG,
    )
    if (isError(res)) {
      throw new Error(getApiErrorMessage(res, 'Не удалось обновить стикер'))
    }

    return mapFiltersFromApi(res.data?.data?.stickers)
  },

  async deleteFilter(filterId: number): Promise<TaskTrackerFilter[]> {
    const res = await API.post<ResponseWrapper<ApiTaskTrackerFiltersDeleteResponse>>(
      `task-tracker-fields/delete${getParams({ field_setting_id: filterId })}`,
      {},
      TASK_TRACKER_API_REQUEST_CONFIG,
    )
    if (isError(res)) {
      throw new Error(getApiErrorMessage(res, 'Не удалось удалить стикер'))
    }

    return mapFiltersFromApi(res.data?.data?.stickers)
  },

  async getBoardStickers(boardId: number): Promise<TaskTrackerBoardStickersDetails> {
    const res = await API.get<ResponseWrapper<ApiTaskTrackerBoardStickersResponse>>(
      `task-tracker-fields/board${getParams({ board_id: boardId })}`,
      TASK_TRACKER_API_REQUEST_CONFIG,
    )
    if (isError(res)) {
      throw new Error(
        getApiErrorMessage(res, 'Не удалось загрузить стикеры доски'),
      )
    }

    const data = res.data?.data
    if (!data?.board) {
      throw new Error('Не удалось загрузить стикеры доски')
    }

    return {
      board: mapBoardFromApi(data.board),
      stickers: mapBoardStickersFromApi(data.stickers),
    }
  },

  async setBoardStickerVisibility(
    boardId: number,
    payload: SetTaskTrackerBoardStickerVisibilityRequest,
  ): Promise<TaskTrackerBoardStickersDetails> {
    const res = await API.post<ResponseWrapper<ApiTaskTrackerBoardStickersResponse>>(
      `task-tracker-fields/set-visibility${getParams({ board_id: boardId })}`,
      {
        field_setting_id: payload.fieldSettingId,
        is_visible: payload.isVisible,
      },
      TASK_TRACKER_API_REQUEST_CONFIG,
    )
    if (isError(res)) {
      throw new Error(
        getApiErrorMessage(res, 'Не удалось обновить видимость стикера'),
      )
    }

    const data = res.data?.data
    if (!data?.board) {
      throw new Error('Не удалось обновить видимость стикера')
    }

    return {
      board: mapBoardFromApi(data.board),
      stickers: mapBoardStickersFromApi(data.stickers),
    }
  },

  async setBoardStickerPosition(
    boardId: number,
    payload: SetTaskTrackerBoardStickerPositionRequest,
  ): Promise<TaskTrackerBoardStickersDetails> {
    const res = await API.post<ResponseWrapper<ApiTaskTrackerBoardStickersResponse>>(
      `task-tracker-fields/set-position${getParams({ board_id: boardId })}`,
      {
        field_setting_id: payload.fieldSettingId,
        position: payload.position,
      },
      TASK_TRACKER_API_REQUEST_CONFIG,
    )
    if (isError(res)) {
      throw new Error(
        getApiErrorMessage(res, 'Не удалось изменить порядок стикера'),
      )
    }

    const data = res.data?.data
    if (!data?.board) {
      throw new Error('Не удалось изменить порядок стикера')
    }

    return {
      board: mapBoardFromApi(data.board),
      stickers: mapBoardStickersFromApi(data.stickers),
    }
  },

  async setBoardStickerRequired(
    boardId: number,
    payload: SetTaskTrackerBoardStickerRequiredRequest,
  ): Promise<TaskTrackerBoardStickersDetails> {
    const res = await API.post<ResponseWrapper<ApiTaskTrackerBoardStickersResponse>>(
      `task-tracker-fields/set-required${getParams({ board_id: boardId })}`,
      {
        field_setting_id: payload.fieldSettingId,
        is_required: payload.isRequired,
      },
      TASK_TRACKER_API_REQUEST_CONFIG,
    )
    if (isError(res)) {
      throw new Error(
        getApiErrorMessage(res, 'Не удалось обновить обязательность стикера'),
      )
    }

    const data = res.data?.data
    if (!data?.board) {
      throw new Error('Не удалось обновить обязательность стикера')
    }

    return {
      board: mapBoardFromApi(data.board),
      stickers: mapBoardStickersFromApi(data.stickers),
    }
  },

  async setTaskStickerValue(
    taskId: number,
    payload: SetTaskTrackerTaskStickerValueRequest,
  ): Promise<TaskTrackerBoardCard> {
    const res = await API.post<ResponseWrapper<ApiBoardCard>>(
      `task-tracker-fields/set-value${getParams({ task_id: taskId })}`,
      {
        field_setting_id: payload.fieldSettingId,
        value: payload.value,
      },
      TASK_TRACKER_API_REQUEST_CONFIG,
    )
    if (isError(res)) {
      throw new Error(
        getApiErrorMessage(res, 'Не удалось сохранить значение стикера'),
      )
    }
    if (!res.data?.data) {
      throw new Error('Не удалось сохранить значение стикера')
    }

    return mapBoardCardFromApi(res.data.data)
  },
}
